/**
 * Accessibility Widget JavaScript
 * ADA/WCAG Compliant Accessibility Features
 */

class AccessibilityWidget extends HTMLElement {
  constructor() {
    super();
    this.settings = this.loadSettings();
    this.isOpen = false;
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
    this.applySettings();
    this.setupKeyboardNavigation();
    this.setupReadingGuide();
  }

  loadSettings() {
    try {
      const saved = localStorage.getItem('accessibilitySettings');
      return saved ? JSON.parse(saved) : this.getDefaultSettings();
    } catch (e) {
      return this.getDefaultSettings();
    }
  }

  getDefaultSettings() {
    return {
      seizureSafe: false,
      visionImpaired: false,
      adhdFriendly: false,
      cognitiveDisability: false,
      keyboardNavigation: false,
      screenReader: false,
      colorMode: 'default',
      fontSize: 0,
      textSpacing: 0,
      lineHeight: 0,
      highlightLinks: false,
      highlightTitles: false,
      stopAnimations: false,
      largeCursor: false,
      readingGuide: false
    };
  }

  saveSettings() {
    try {
      localStorage.setItem('accessibilitySettings', JSON.stringify(this.settings));
    } catch (e) {
      console.warn('Could not save accessibility settings');
    }
  }

  render() {
    this.innerHTML = `
      <button class="accessibility-widget-toggle" aria-label="Accessibility Options" aria-expanded="false" aria-controls="accessibility-panel">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9H15V22H13V16H11V22H9V9H3V7H21V9Z"/>
        </svg>
      </button>
      
      <div class="accessibility-widget-panel" id="accessibility-panel" role="dialog" aria-label="Accessibility Adjustments" aria-modal="true">
        <div class="accessibility-widget-header">
          <h2>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white" aria-hidden="true">
              <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9H15V22H13V16H11V22H9V9H3V7H21V9Z"/>
            </svg>
            Accessibility Adjustments
          </h2>
          <div class="accessibility-widget-header-actions">
            <button class="accessibility-reset-btn" aria-label="Reset all settings">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4.01 7.58 4.01 12C4.01 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z"/>
              </svg>
              Reset
            </button>
            <button class="accessibility-widget-close" aria-label="Close accessibility panel">×</button>
          </div>
        </div>
        
        <div class="accessibility-widget-content">
          <div class="accessibility-widget-section-title">Accessibility Profiles</div>
          
          ${this.renderProfileOption('seizureSafe', 'Seizure Safe Profile', 'Clear flashes & reduces color', this.getSeizureIcon())}
          ${this.renderProfileOption('visionImpaired', 'Vision Impaired Profile', 'Enhances website\'s visuals', this.getVisionIcon())}
          ${this.renderProfileOption('adhdFriendly', 'ADHD Friendly Profile', 'More focus & fewer distractions', this.getADHDIcon())}
          ${this.renderProfileOption('cognitiveDisability', 'Cognitive Disability Profile', 'Assists with reading and focusing', this.getCognitiveIcon())}
          ${this.renderProfileOption('keyboardNavigation', 'Keyboard Navigation (Motor)', 'Use website with the keyboard', this.getKeyboardIcon())}
          ${this.renderProfileOption('screenReader', 'Blind Users (Screen Reader)', 'Optimize website for screen-readers', this.getScreenReaderIcon())}
          
          <div class="accessibility-widget-section-title">Content Adjustments</div>
          
          ${this.renderSliderOption('fontSize', 'Content Scaling', '100%')}
          ${this.renderSliderOption('textSpacing', 'Text Spacing', 'Default')}
          ${this.renderSliderOption('lineHeight', 'Line Height', 'Default')}
          
          <div class="accessibility-widget-section-title">Color Adjustments</div>
          
          <div class="accessibility-color-options" role="group" aria-label="Color mode selection">
            <button class="accessibility-color-btn default ${this.settings.colorMode === 'default' ? 'active' : ''}" data-color="default" aria-label="Default colors" title="Default"></button>
            <button class="accessibility-color-btn dark ${this.settings.colorMode === 'dark' ? 'active' : ''}" data-color="dark" aria-label="Dark mode" title="Dark Mode"></button>
            <button class="accessibility-color-btn light ${this.settings.colorMode === 'light' ? 'active' : ''}" data-color="light" aria-label="Light mode" title="Light Mode"></button>
            <button class="accessibility-color-btn high-contrast ${this.settings.colorMode === 'high-contrast' ? 'active' : ''}" data-color="high-contrast" aria-label="High contrast" title="High Contrast"></button>
            <button class="accessibility-color-btn sepia ${this.settings.colorMode === 'sepia' ? 'active' : ''}" data-color="sepia" aria-label="Sepia mode" title="Sepia"></button>
          </div>
          
          <div class="accessibility-widget-section-title">Quick Actions</div>
          
          <div class="accessibility-quick-actions">
            <button class="accessibility-quick-btn ${this.settings.highlightLinks ? 'active' : ''}" data-action="highlightLinks" aria-pressed="${this.settings.highlightLinks}">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>
              Highlight Links
            </button>
            <button class="accessibility-quick-btn ${this.settings.highlightTitles ? 'active' : ''}" data-action="highlightTitles" aria-pressed="${this.settings.highlightTitles}">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4v3h5.5v12h3V7H19V4z"/></svg>
              Highlight Titles
            </button>
            <button class="accessibility-quick-btn ${this.settings.stopAnimations ? 'active' : ''}" data-action="stopAnimations" aria-pressed="${this.settings.stopAnimations}">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
              Stop Animations
            </button>
            <button class="accessibility-quick-btn ${this.settings.largeCursor ? 'active' : ''}" data-action="largeCursor" aria-pressed="${this.settings.largeCursor}">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M13.64 21.97C13.14 22.21 12.54 22 12.31 21.5L10.13 16.76L7.62 18.78C7.45 18.92 7.24 19 7.02 19C6.55 19 6.16 18.61 6.16 18.14V4.86C6.16 4.39 6.55 4 7.02 4C7.24 4 7.46 4.08 7.63 4.23L18.84 14.42C19.23 14.76 19.27 15.35 18.93 15.75C18.8 15.89 18.63 16 18.44 16.04L14.73 16.69L16.93 21.5C17.16 22 16.95 22.61 16.45 22.84L13.64 21.97Z"/></svg>
              Large Cursor
            </button>
            <button class="accessibility-quick-btn ${this.settings.readingGuide ? 'active' : ''}" data-action="readingGuide" aria-pressed="${this.settings.readingGuide}">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
              Reading Guide
            </button>
            <button class="accessibility-quick-btn" data-action="readPage" aria-label="Read page aloud">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
              Read Page
            </button>
          </div>
        </div>
        
        <div class="accessibility-widget-footer">
          <a href="/pages/accessibility" aria-label="View accessibility statement">Accessibility Statement</a>
        </div>
      </div>
    `;
  }

  renderProfileOption(key, title, description, icon) {
    return `
      <div class="accessibility-profile-option" role="group" aria-label="${title}">
        <div class="accessibility-profile-info">
          <div class="accessibility-profile-icon">${icon}</div>
          <div class="accessibility-profile-text">
            <h3>${title}</h3>
            <p>${description}</p>
          </div>
        </div>
        <div class="accessibility-toggle">
          <button class="accessibility-toggle-btn off ${!this.settings[key] ? 'active' : ''}" data-profile="${key}" data-value="false" aria-label="Turn off ${title}">OFF</button>
          <button class="accessibility-toggle-btn on ${this.settings[key] ? 'active' : ''}" data-profile="${key}" data-value="true" aria-label="Turn on ${title}">ON</button>
        </div>
      </div>
    `;
  }

  renderSliderOption(key, title, valueLabel) {
    const values = { fontSize: ['100%', '110%', '125%', '150%'], textSpacing: ['Default', 'Medium', 'Large', 'Extra Large'], lineHeight: ['Default', '1.5x', '1.8x', '2.2x'] };
    const currentValue = this.settings[key] || 0;
    return `
      <div class="accessibility-slider-option">
        <div class="accessibility-slider-header">
          <div class="accessibility-slider-info">
            <div class="accessibility-slider-icon">${this.getScaleIcon()}</div>
            <span class="accessibility-slider-text">${title}</span>
          </div>
          <span class="accessibility-slider-value" data-value-display="${key}">${values[key][currentValue]}</span>
        </div>
        <input type="range" class="accessibility-slider" data-slider="${key}" min="0" max="3" value="${currentValue}" aria-label="${title}" aria-valuetext="${values[key][currentValue]}">
      </div>
    `;
  }

  getSeizureIcon() {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 2v11h3v9l7-12h-4l4-8z"/></svg>`;
  }

  getVisionIcon() {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>`;
  }

  getADHDIcon() {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>`;
  }

  getCognitiveIcon() {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>`;
  }

  getKeyboardIcon() {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 5H4c-1.1 0-1.99.9-1.99 2L2 17c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-9 3h2v2h-2V8zm0 3h2v2h-2v-2zM8 8h2v2H8V8zm0 3h2v2H8v-2zm-1 2H5v-2h2v2zm0-3H5V8h2v2zm9 7H8v-2h8v2zm0-4h-2v-2h2v2zm0-3h-2V8h2v2zm3 3h-2v-2h2v2zm0-3h-2V8h2v2z"/></svg>`;
  }

  getScreenReaderIcon() {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>`;
  }

  getScaleIcon() {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 3l2.3 2.3-2.89 2.87 1.42 1.42L18.7 6.7 21 9V3h-6zM3 9l2.3-2.3 2.87 2.89 1.42-1.42L6.7 5.3 9 3H3v6zm6 12l-2.3-2.3 2.89-2.87-1.42-1.42L5.3 17.3 3 15v6h6zm12-6l-2.3 2.3-2.87-2.89-1.42 1.42 2.89 2.87L15 21h6v-6z"/></svg>`;
  }

  attachEventListeners() {
    // Toggle button
    const toggleBtn = this.querySelector('.accessibility-widget-toggle');
    toggleBtn.addEventListener('click', () => this.togglePanel());

    // Close button
    const closeBtn = this.querySelector('.accessibility-widget-close');
    closeBtn.addEventListener('click', () => this.closePanel());

    // Reset button
    const resetBtn = this.querySelector('.accessibility-reset-btn');
    resetBtn.addEventListener('click', () => this.resetSettings());

    // Profile toggles
    this.querySelectorAll('.accessibility-toggle-btn').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleProfileToggle(e));
    });

    // Sliders
    this.querySelectorAll('.accessibility-slider').forEach(slider => {
      slider.addEventListener('input', (e) => this.handleSliderChange(e));
    });

    // Color buttons
    this.querySelectorAll('.accessibility-color-btn').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleColorChange(e));
    });

    // Quick action buttons
    this.querySelectorAll('.accessibility-quick-btn').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleQuickAction(e));
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closePanel();
      }
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (this.isOpen && !this.contains(e.target)) {
        this.closePanel();
      }
    });
  }

  togglePanel() {
    this.isOpen = !this.isOpen;
    const panel = this.querySelector('.accessibility-widget-panel');
    const toggle = this.querySelector('.accessibility-widget-toggle');
    
    panel.classList.toggle('active', this.isOpen);
    toggle.setAttribute('aria-expanded', this.isOpen);

    if (this.isOpen) {
      // Focus first interactive element in panel
      const firstFocusable = panel.querySelector('button, input');
      if (firstFocusable) firstFocusable.focus();
    }
  }

  closePanel() {
    this.isOpen = false;
    const panel = this.querySelector('.accessibility-widget-panel');
    const toggle = this.querySelector('.accessibility-widget-toggle');
    
    panel.classList.remove('active');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.focus();
  }

  handleProfileToggle(e) {
    const btn = e.target;
    const profile = btn.dataset.profile;
    const value = btn.dataset.value === 'true';
    
    this.settings[profile] = value;
    this.saveSettings();
    this.applySettings();
    
    // Update UI
    const container = btn.closest('.accessibility-toggle');
    container.querySelectorAll('.accessibility-toggle-btn').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
  }

  handleSliderChange(e) {
    const slider = e.target;
    const key = slider.dataset.slider;
    const value = parseInt(slider.value);
    
    this.settings[key] = value;
    this.saveSettings();
    this.applySettings();
    
    // Update value display
    const values = { fontSize: ['100%', '110%', '125%', '150%'], textSpacing: ['Default', 'Medium', 'Large', 'Extra Large'], lineHeight: ['Default', '1.5x', '1.8x', '2.2x'] };
    const display = this.querySelector(`[data-value-display="${key}"]`);
    if (display) display.textContent = values[key][value];
    slider.setAttribute('aria-valuetext', values[key][value]);
  }

  handleColorChange(e) {
    const btn = e.target;
    const color = btn.dataset.color;
    
    this.settings.colorMode = color;
    this.saveSettings();
    this.applySettings();
    
    // Update UI
    this.querySelectorAll('.accessibility-color-btn').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
  }

  handleQuickAction(e) {
    const btn = e.target.closest('.accessibility-quick-btn');
    const action = btn.dataset.action;
    
    if (action === 'readPage') {
      this.readPageAloud();
      return;
    }
    
    this.settings[action] = !this.settings[action];
    this.saveSettings();
    this.applySettings();
    
    // Update UI
    btn.classList.toggle('active', this.settings[action]);
    btn.setAttribute('aria-pressed', this.settings[action]);
  }

  applySettings() {
    const body = document.body;
    
    // Clear all accessibility classes
    body.classList.remove(
      'a11y-seizure-safe', 'a11y-vision-impaired', 'a11y-adhd-friendly',
      'a11y-cognitive-disability', 'a11y-keyboard-navigation', 'a11y-screen-reader',
      'a11y-dark-mode', 'a11y-light-mode', 'a11y-high-contrast', 'a11y-sepia',
      'a11y-font-size-1', 'a11y-font-size-2', 'a11y-font-size-3',
      'a11y-text-spacing-1', 'a11y-text-spacing-2', 'a11y-text-spacing-3',
      'a11y-line-height-1', 'a11y-line-height-2', 'a11y-line-height-3',
      'a11y-highlight-links', 'a11y-highlight-titles', 'a11y-stop-animations',
      'a11y-large-cursor', 'a11y-reading-guide'
    );
    
    // Apply profile settings
    if (this.settings.seizureSafe) body.classList.add('a11y-seizure-safe');
    if (this.settings.visionImpaired) body.classList.add('a11y-vision-impaired');
    if (this.settings.adhdFriendly) body.classList.add('a11y-adhd-friendly');
    if (this.settings.cognitiveDisability) body.classList.add('a11y-cognitive-disability');
    if (this.settings.keyboardNavigation) body.classList.add('a11y-keyboard-navigation');
    if (this.settings.screenReader) body.classList.add('a11y-screen-reader');
    
    // Apply color mode
    const colorClasses = { dark: 'a11y-dark-mode', light: 'a11y-light-mode', 'high-contrast': 'a11y-high-contrast', sepia: 'a11y-sepia' };
    if (colorClasses[this.settings.colorMode]) body.classList.add(colorClasses[this.settings.colorMode]);
    
    // Apply font size
    if (this.settings.fontSize > 0) body.classList.add(`a11y-font-size-${this.settings.fontSize}`);
    
    // Apply text spacing
    if (this.settings.textSpacing > 0) body.classList.add(`a11y-text-spacing-${this.settings.textSpacing}`);
    
    // Apply line height
    if (this.settings.lineHeight > 0) body.classList.add(`a11y-line-height-${this.settings.lineHeight}`);
    
    // Apply quick actions
    if (this.settings.highlightLinks) body.classList.add('a11y-highlight-links');
    if (this.settings.highlightTitles) body.classList.add('a11y-highlight-titles');
    if (this.settings.stopAnimations) body.classList.add('a11y-stop-animations');
    if (this.settings.largeCursor) body.classList.add('a11y-large-cursor');
    if (this.settings.readingGuide) body.classList.add('a11y-reading-guide');
  }

  resetSettings() {
    this.settings = this.getDefaultSettings();
    this.saveSettings();
    this.applySettings();
    this.render();
    this.attachEventListeners();
    
    // Announce reset to screen readers
    this.announceToScreenReader('Accessibility settings have been reset to defaults');
  }

  setupKeyboardNavigation() {
    // Trap focus within panel when open
    const panel = this.querySelector('.accessibility-widget-panel');
    const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    
    panel.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || !this.isOpen) return;
      
      const focusable = panel.querySelectorAll(focusableElements);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  setupReadingGuide() {
    document.addEventListener('mousemove', (e) => {
      if (this.settings.readingGuide) {
        document.body.style.setProperty('--reading-guide-top', `${e.clientY - 20}px`);
      }
    });
  }

  readPageAloud() {
    if ('speechSynthesis' in window) {
      // Stop any current speech
      window.speechSynthesis.cancel();
      
      // Get main content
      const mainContent = document.querySelector('main') || document.body;
      const text = mainContent.innerText;
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 1;
      
      window.speechSynthesis.speak(utterance);
      this.announceToScreenReader('Reading page aloud. Press Escape to stop.');
      
      // Stop on escape
      const stopHandler = (e) => {
        if (e.key === 'Escape') {
          window.speechSynthesis.cancel();
          document.removeEventListener('keydown', stopHandler);
        }
      };
      document.addEventListener('keydown', stopHandler);
    } else {
      this.announceToScreenReader('Text-to-speech is not supported in your browser');
    }
  }

  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'visually-hidden';
    announcement.textContent = message;
    document.body.appendChild(announcement);
    
    setTimeout(() => announcement.remove(), 1000);
  }
}

// Register custom element
customElements.define('accessibility-widget', AccessibilityWidget);

// Initialize widget when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  if (!document.querySelector('accessibility-widget')) {
    const widget = document.createElement('accessibility-widget');
    document.body.appendChild(widget);
  }
});
